import React, { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import { SvgIcon, Tooltip } from '@mui/material'
import { ColumnVersionFull, CellSnapshot } from '../types'
import { TppMatrixState } from '../../TppView/types'
import { ReactComponent as LockIcon } from '../../../assets/images/actions/lock.svg'
import { ReactComponent as HighlightIcon } from '../../../assets/images/differentiating_icon.svg'
import { ReactComponent as CloneIcon } from '../../../assets/images/cloneto_icon.svg'

interface VersionPreviewContentProps {
    version: ColumnVersionFull
    matrixState: TppMatrixState
}

interface RowWithCells {
    rowId: string
    rowTitle: string
    sectionTitle: string
    cells: CellSnapshot[]
}

const VersionPreviewContent = ({ version, matrixState }: VersionPreviewContentProps) => {
    // Group cells by row using the slot -> row mapping from matrixState
    const rowsWithCells = useMemo(() => {
        const { slots, rows, sections } = matrixState
        const rowMap = new Map<string, RowWithCells>()

        // Iterate through sections and rows in order to maintain proper ordering
        const orderedRows: RowWithCells[] = []
        const sectionIds = matrixState.tpp?.sectionIds || []

        for (const sectionId of sectionIds) {
            const section = sections[sectionId]
            if (!section) continue

            for (const rowId of section.rowIds) {
                const row = rows[rowId]
                if (!row) continue

                const rowData: RowWithCells = {
                    rowId,
                    rowTitle: row.title,
                    sectionTitle: section.title,
                    cells: [],
                }
                rowMap.set(rowId, rowData)
                orderedRows.push(rowData)
            }
        }

        // Map version cells to their rows via slots
        for (const cell of version.cells) {
            const slot = slots[cell.slotId]
            if (!slot) continue

            const rowData = rowMap.get(slot.rowId)
            if (rowData) {
                rowData.cells.push(cell)
            }
        }

        // Filter out rows with no cells
        return orderedRows.filter((row) => row.cells.length > 0)
    }, [version.cells, matrixState])

    return (
        <Box
            sx={{
                flex: 1,
                overflow: 'auto',
                px: 1,
            }}
        >
            <Stack gap={0}>
                {rowsWithCells.map((row) => (
                    <Box
                        key={row.rowId}
                        sx={{
                            display: 'flex',
                            borderBottom: '1px solid',
                            borderColor: 'figma.grayscale.stroke',
                        }}
                    >
                        {/* Row title column */}
                        <Box
                            sx={{
                                width: 180,
                                minWidth: 180,
                                p: 2,
                                borderRight: '1px solid',
                                borderColor: 'figma.grayscale.stroke',
                                bgcolor: 'figma.grayscale.tier6',
                            }}
                        >
                            <Typography variant="14x600" color="figma.grayscale.textHier1">
                                {row.rowTitle}
                            </Typography>
                        </Box>

                        {/* Cells column */}
                        <Stack flex={1} gap={1} p={1.5}>
                            {row.cells.map((cell) => (
                                <CellPreview key={cell._id} cell={cell} />
                            ))}
                        </Stack>
                    </Box>
                ))}

                {rowsWithCells.length === 0 && (
                    <Stack alignItems="center" justifyContent="center" py={6}>
                        <Typography variant="14x400" color="figma.grayscale.textHier2">
                            No cells in this version
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Box>
    )
}

interface CellPreviewProps {
    cell: CellSnapshot
}

const CellPreview = ({ cell }: CellPreviewProps) => {
    const hasContent = cell.content && cell.content.trim().length > 0
    const displayContent = cell.isClone ? 'Cloned cell' : cell.content || 'Empty cell'

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: cell.isHighlighted ? 'figma.primary3' : 'figma.grayscale.tier7',
                opacity: cell.locked ? 0.7 : 1,
            }}
        >
            <Typography
                variant="14x400"
                color={hasContent && !cell.isClone ? 'figma.grayscale.textHier1' : 'figma.grayscale.textHier2'}
                sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontStyle: !hasContent || cell.isClone ? 'italic' : 'normal',
                }}
            >
                {displayContent}
            </Typography>

            {/* Cell status icons */}
            <Stack direction="row" gap={1} mt={1.5} alignItems="center">
                {cell.isHighlighted && (
                    <Tooltip title="Highlighted" placement="top">
                        <SvgIcon
                            component={HighlightIcon}
                            inheritViewBox
                            fontSize="small"
                            sx={{ fontSize: '18px', color: 'figma.primary' }}
                        />
                    </Tooltip>
                )}
                {cell.locked && (
                    <Tooltip title={`Locked by ${cell.locked.lockedBy.name}`} placement="top">
                        <SvgIcon
                            component={LockIcon}
                            inheritViewBox
                            fontSize="small"
                            sx={{ fontSize: '18px', color: 'figma.primary' }}
                        />
                    </Tooltip>
                )}
                {cell.isClone && (
                    <Tooltip title="Cloned cell" placement="top">
                        <SvgIcon
                            component={CloneIcon}
                            inheritViewBox
                            fontSize="small"
                            sx={{ fontSize: '18px', color: 'figma.primary' }}
                        />
                    </Tooltip>
                )}
            </Stack>
        </Paper>
    )
}

export default VersionPreviewContent
